import React , { useState, useEffect }from 'react';
import Modal from '../../../../Components/Modal';
import {
    MDBRow,
    MDBCol
} from 'mdb-react-ui-kit';
import Button from '../../../../Components/Button';
import InputField from '../../../../Components/InputField';
import useAxiosCall from '../../../../Hooks/useAxiosCall';
import SelectField from '../../../../Components/SelectField';
import { MDBTable, MDBTableHead, MDBTableBody, MDBCheckbox } from 'mdb-react-ui-kit';
import UserValidator from '../../../../Components/UserValidator';
import { SecureStorage } from '../../../../Utilities/StorageEncryption';
import { encryptData, decryptData } from '../../../../Utilities/CryptoUtility';

const AddUser = ({isOpen, onClose, onUpdateSuccess, onUpdateError, editMode}) => {
    const [domains, setDomains] = useState([]);
    const [domainsLoading, setDomainsLoading] = useState(false);
    
    const [formData, setFormData] = useState({
        username: '',
        windows_username: '',
        application: '',
        first_name: '',
        last_name: '',
        preferred_name: '',
        email: '',
        cell_phone: '',
        is_administrator: false,
        is_inactive: false,
        select_all_domains: false,
        domain_access: [],
        domain_defaults: []
    });

    const [initialFormData, setInitialFormData] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showCreateConfirmModal, setShowCreateConfirmModal] = useState(false);
    const { makeApiCall } = useAxiosCall();
    const [updated, setUpdated] = useState(false);
    const validator = UserValidator();
    const [userAdded, setUserAdded] = useState(false);

    // Function to check if there are any changes
    const hasChanges = () => {
        if (!initialFormData) return false;
        
        return JSON.stringify(formData) !== JSON.stringify(initialFormData);
    };

    const closeModal = () => {
        if (hasChanges()) {
            setShowConfirmModal(true);
        } else {
            onClose(userAdded);
            resetForm();
        }
    };

    const confirmClose = () => {
        setShowConfirmModal(false);
        onClose(userAdded);
        resetForm();
    };

    const cancelClose = () => {
        setShowConfirmModal(false);
    };

    useEffect(() => {
        if (isOpen) {
            fetchDomains();
        }
    }, [isOpen]);

    const fetchDomains = async () => {
        setDomainsLoading(true);
        try {
            const companyKey = SecureStorage.getItem('companyKey') || 'gva';
            
            await makeApiCall({
                onSuccess: (response) => {
                    if (response.data) {
                        const decryptedData = decryptData(response.data);
                        
                        if (decryptedData && decryptedData.domains) {
                            setDomains(decryptedData.domains);
                            const domainCount = decryptedData.domains.length;
                            const newFormData = {
                                username: '',
                                windows_username: '',
                                application: '',
                                first_name: '',
                                last_name: '',
                                preferred_name: '',
                                email: '',
                                cell_phone: '',
                                is_administrator: false,
                                is_inactive: false,
                                select_all_domains: false,
                                domain_access: new Array(domainCount).fill(false),
                                domain_defaults: new Array(domainCount).fill(false)
                            };
                            setFormData(newFormData);
                            setInitialFormData(JSON.parse(JSON.stringify(newFormData))); // Deep copy for initial state
                        }
                    }
                },
                onError: (error) => {
                    console.error('Error fetching domains:', error);
                    handleApiCallError('Failed to load domains');
                },
                requestType: 'GET',
                url: `getDomains?company=${companyKey}`,
                loader: true
            });
        } catch (error) {
            console.error('Error in fetchDomains:', error);
        } finally {
            setDomainsLoading(false);
        }
    };

    const resetForm = () => {
        const domainCount = domains.length;
        setFormData({
            username: '',
            windows_username: '',
            application: '',
            first_name: '',
            last_name: '',
            preferred_name: '',
            email: '',
            cell_phone: '',
            is_administrator: false,
            is_inactive: false,
            select_all_domains: false,
            domain_access: new Array(domainCount).fill(false),
            domain_defaults: new Array(domainCount).fill(false)
        });
        validator.hideMessages();
    };
    
    const handleApiCallSuccess = (data) => {
        resetForm();
        // Update initialFormData to match the reset form state
        const domainCount = domains.length;
        const resetFormData = {
            username: '',
            windows_username: '',
            application: '',
            first_name: '',
            last_name: '',
            preferred_name: '',
            email: '',
            cell_phone: '',
            is_administrator: false,
            is_inactive: false,
            select_all_domains: false,
            domain_access: new Array(domainCount).fill(false),
            domain_defaults: new Array(domainCount).fill(false)
        };
        setInitialFormData(JSON.parse(JSON.stringify(resetFormData)));
        
        if (onUpdateSuccess) {
            onUpdateSuccess(data.message);
            setUserAdded(true);
        }
    };
    
    const handleApiCallError = (errorMessage) => {
        if (onUpdateError) {
            onUpdateError(errorMessage);
        }
    };

    const onSubmit = async (event) => {
        event.preventDefault();
        console.log('Submit clicked');

        if (validator.allValid()) {
            // Validate that at least one domain is selected
            const hasSelectedDomain = formData.domain_access && formData.domain_access.some(access => access === true);
            
            if (!hasSelectedDomain) {
                if (onUpdateError) {
                    onUpdateError('Please select at least one domain for the user.');
                }
                return;
            }

            // Show confirmation modal instead of directly creating user
            setShowCreateConfirmModal(true);
        } else {
            validator.showMessages();
            setUpdated(!updated);
        }
    };

    const handleCreateUser = async () => {
        // Close confirmation modal
        setShowCreateConfirmModal(false);
        
        try {
            const companyPrefix = String(SecureStorage.getItem('companyKey') ?? '');
            
            // Build domain access array based on form data and actual domains
            const domainAccessArray = [];
            for (let i = 0; i < formData.domain_access.length; i++) {
                if (formData.domain_access[i] && domains[i]) {
                    domainAccessArray.push({
                        domainName: domains[i].text || domains[i].name,
                        hasAccess: true,
                        isDefault: formData.domain_defaults[i]
                    });
                }
            }

            // Structure the data to avoid stdClass issues with middleware
            // Send complex objects as JSON strings so middleware can handle them
            const apiPayload = {
                // Account data as JSON string
                account: JSON.stringify({
                    companyPrefix: companyPrefix,
                    username: '', // Ensure it's not undefined
                    firstName: formData.first_name,
                    lastName: formData.last_name,
                    preferredName: formData.preferred_name,
                    email: formData.email,
                    cellPhone: formData.cell_phone,
                    isAdministrator: formData.is_administrator,
                    isInactive: formData.is_inactive
                }),
                // Domain access data as JSON string
                domainAccess: JSON.stringify({
                    companyPrefix: companyPrefix,
                    username: '', // Ensure it's not undefined
                    domainAccess: domainAccessArray
                })
            };

            // Keep the original encryption wrapper format
            const requestData = {
                data: encryptData(JSON.stringify(apiPayload))
            };

            console.log('Request data prepared:', requestData);

            await makeApiCall({
                requestData,
                onSuccess: (response) => {
                    console.log('Response data on success:', response.message);
                    handleApiCallSuccess(response);
                },
                onError: handleApiCallError,
                requestType: 'POST',
                url: 'addUserAccount',
                loader: true
            });
        } catch (error) {
            console.error('Error adding user:', error);
            handleApiCallError('An error occurred while adding the user.');
        }
    };

    const cancelCreateUser = () => {
        setShowCreateConfirmModal(false);
    };

    const handleSelectAllDomains = (e) => {
        const newValue = e.target.checked;
        const updatedDomainAccess = formData.domain_access.map(() => newValue);
        setFormData({
            ...formData, 
            select_all_domains: newValue,
            domain_access: updatedDomainAccess
        });
    };

    const handleDomainAccessChange = (index, checked) => {
        const updatedDomainAccess = [...formData.domain_access];
        updatedDomainAccess[index] = checked;
        
        const allChecked = updatedDomainAccess.every(access => access);
        
        setFormData({
            ...formData,
            domain_access: updatedDomainAccess,
            select_all_domains: allChecked
        });
    };

    const handleDomainDefaultChange = (index, checked) => {
        const updatedDomainDefaults = [...formData.domain_defaults];
        
        if (checked) {
            // If setting as default, first uncheck all other defaults
            updatedDomainDefaults.fill(false);
            updatedDomainDefaults[index] = true;
            
            // Ensure the domain has access if setting as default
            if (!formData.domain_access[index]) {
                const updatedDomainAccess = [...formData.domain_access];
                updatedDomainAccess[index] = true;
                setFormData({
                    ...formData,
                    domain_access: updatedDomainAccess,
                    domain_defaults: updatedDomainDefaults
                });
            } else {
                setFormData({
                    ...formData,
                    domain_defaults: updatedDomainDefaults
                });
            }
        } else {
            // If unchecking, just uncheck this domain
            updatedDomainDefaults[index] = false;
            setFormData({
                ...formData,
                domain_defaults: updatedDomainDefaults
            });
        }
    };

    const handleNameChange = (field, value) => {
        const updatedFormData = { ...formData, [field]: value };
        const firstName = field === 'first_name' ? value : formData.first_name;
        const lastName = field === 'last_name' ? value : formData.last_name;
        
        if (firstName && lastName) {
            const pbUsername = (firstName + '_' + lastName).toLowerCase().replace(/\s+/g, '');
            const windowsUsername = (firstName.charAt(0) + lastName).toLowerCase().replace(/\s+/g, '');
            const companyKey = SecureStorage.getItem('companyKey');
            // Prepend company key to usernames
            // updatedFormData.username = pbUsername;
            // updatedFormData.windows_username = companyKey ? `${companyKey}_${windowsUsername}` : windowsUsername;
        }  
        
        // console.log('Generated username:', updatedFormData.username);
        setFormData(updatedFormData);
    };

    return (
        <>
            <Modal isOpen={isOpen} onClose={closeModal} size="xl">
                <Modal.Header>Add User</Modal.Header>
                <Modal.Body>
                    <MDBRow className='p-2 m-2'>
                        <MDBCol size='md xl lg sm'>
                        <h6>User Information</h6>
                        {/* <FieldWrapper>
                            <InputField label="PB Username" name="username" value={formData.username} id="username" disabled={editMode} onChange={(e) => setFormData({...formData, username: e.target.value})}/>
                            {validator.message('username', formData.username, 'usernameRequired|usernameNoSpace')}
                        </FieldWrapper> */}

                        <FieldWrapper>
                            <InputField label="PropertyBoss Username" name="windows_username" value={formData.windows_username} disabled={editMode} onChange={(e) => setFormData({...formData, windows_username: e.target.value})}/>
                        </FieldWrapper>

                        {/* <FieldWrapper>
                            <InputField label="Application" name="application" value={formData.application} disabled={editMode} onChange={(e) => setFormData({...formData, application: e.target.value})}/>
                        </FieldWrapper> */}

                        <FieldWrapper>
                            <InputField 
                                label="First Name" 
                                name="first_name" 
                                value={formData.first_name}
                                onChange={(e) => handleNameChange('first_name', e.target.value)}
                            />
                            {validator.message('first_name', formData.first_name, 'firstnameRequired|usernameNoSpace')}
                        </FieldWrapper>

                        <FieldWrapper>
                            <InputField 
                                label="Last Name" 
                                name="last_name" 
                                value={formData.last_name}
                                onChange={(e) => handleNameChange('last_name', e.target.value)}
                            />
                            {validator.message('last_name', formData.last_name, 'lastnameRequired|usernameNoSpace')}
                        </FieldWrapper>

                        <FieldWrapper>
                            <InputField label="Preferred Name" name="preferred_name" value={formData.preferred_name} onChange={(e) => setFormData({...formData, preferred_name: e.target.value})}/>
                            {validator.message('preferred_name', formData.preferred_name, 'preferrednameRequired')}
                        </FieldWrapper>

                        <FieldWrapper>
                            <InputField label="Email Address" name="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})}/>
                            {validator.message('email', formData.email, 'emailRequired')}
                        </FieldWrapper>

                        <FieldWrapper>
                            <InputField label="Cell Phone Number" name="cell_phone" value={formData.cell_phone} onChange={(e) => setFormData({...formData, cell_phone: e.target.value})}/>
                            {validator.message('cell_phone', formData.cell_phone, 'phoneNoRequired')}
                        </FieldWrapper>

                        <FieldWrapper>
                            <div className="d-flex justify-content-start">
                                <div className="d-flex align-items-center me-4">
                                    <span className="small">Administrator</span>
                                    <MDBCheckbox name="is_administrator" id="is_administrator" checked={formData.is_administrator} onChange={e => setFormData({...formData, is_administrator: e.target.checked})} className="ms-2 form-check-sm"/>
                                </div>
                                
                                {/* <div className="d-flex align-items-center">
                                    <span className="small">Inactive</span>
                                    <MDBCheckbox name="is_inactive" id="is_inactive" checked={formData.is_inactive} onChange={e => setFormData({...formData, is_inactive: e.target.checked})} className="ms-2 form-check-sm"/>
                                </div> */}
                            </div>
                        </FieldWrapper>

                        </MDBCol>
                        <MDBCol size='md xl lg sm'>
                        <h6>Select Domains</h6>
                        
                            <MDBTable align='middle'>
                                <MDBTableHead light>
                                    <tr>
                                    <th scope='col'></th>
                                    <th scope='col'>Access</th>
                                    <th scope='col'>Default</th>
                                    <th scope='col'>Domain</th>
                                    </tr>
                                </MDBTableHead>
                                <MDBTableBody>
                                    <tr>
                                    <td></td>
                                    <td>
                                        <div className="d-flex align-items-center">
                                            <MDBCheckbox name="select_all_domains" id="select_all_domains" checked={formData.select_all_domains} onChange={handleSelectAllDomains}/>
                                            <span className="ms-2">All</span>
                                        </div>
                                    </td>
                                    <td></td>
                                    <td></td>
                                    </tr>
                                    {/* {domainsLoading ? (
                                        <div className="text-center"></div>
                                    ) : ( */}
                                        {domains.map((domain, index) => (
                                            <tr key={index}>
                                                <td>{index + 1}</td>
                                            <th scope='col'>
                                                <MDBCheckbox 
                                                    checked={formData.domain_access[index] || false} 
                                                    onChange={(e) => handleDomainAccessChange(index, e.target.checked)}
                                                />
                                            </th>
                                            <th scope='col'>
                                                <MDBCheckbox 
                                                    checked={formData.domain_defaults[index] || false} 
                                                    onChange={(e) => handleDomainDefaultChange(index, e.target.checked)}
                                                />
                                            </th>
                                            <td>{domain.text || domain.name}</td>
                                        </tr>
                                    ))}
                                {/* )} */}
                                </MDBTableBody>
                            </MDBTable>
                        
                        </MDBCol>
                    </MDBRow>
                </Modal.Body>
                <Modal.Footer>
                    <div className='d-flex justify-content-center'>
                    <Button onClick={onSubmit} className='me-2'>
                        Create
                    </Button>
                    <Button onClick={closeModal} color='secondary'>
                        Cancel
                    </Button>
                    </div>
                </Modal.Footer>
            </Modal>

            {/* Confirmation Modal */}
            <Modal isOpen={showConfirmModal} onClose={cancelClose} size="md">
                <Modal.Header>Confirm Close</Modal.Header>
                <Modal.Body>
                    <p>You have unsaved changes. Are you sure you want to discard these changes?</p>
                </Modal.Body>
                <Modal.Footer>
                    <div className='d-flex justify-content-center'>
                        <Button onClick={confirmClose} color='danger' className='me-2'>
                            Yes, Discard Changes
                        </Button>
                        <Button onClick={cancelClose} color='secondary'>
                            No, Keep Editing
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>

            {/* Create User Confirmation Modal */}
            <Modal isOpen={showCreateConfirmModal} onClose={cancelCreateUser} size="md">
                <Modal.Header>Confirm User Creation</Modal.Header>
                <Modal.Body>
                    <p>By clicking 'Create', I understand I am granting PropertyBoss access for this User and a 'Welcome' message will be sent to their email address. Continue?</p>
                </Modal.Body>
                <Modal.Footer>
                    <div className='d-flex justify-content-center'>
                        <Button onClick={handleCreateUser} className='me-2'>
                            YES, CREATE USER
                        </Button>
                        <Button onClick={cancelCreateUser} color='secondary'>
                            NO, RETURN TO SETUP
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
};
const FieldWrapper = ({ children }) => (
    <div className="mb-3">
      {children}
    </div>
  );
export default AddUser;
